import { ISearchConfig } from 'homepage/types/SectionSearchConfig';
import { SEARCH_CONFIG_TYPES } from 'homepage/enums/contentConstants';
import { IConstructorGroup } from 'shared/types/ConstructorGroup';
import { slugify } from 'shared/utils/consumerSearchSlugify';
import { findTaxonomyPath } from './findTaxonomyPath';

/**
 * [getLinkUrlQueryParams]
 * @param options [string] - options is a string of filters
 * ex: 'num_results_per_page=25&filters[color]=blue&filters[lowest_price_cents]=100-200'
 * we want to transform it to a query understandable by constructor:
 * 'color=blue&lowest_price_cents=100-200'
 */
const getLinkUrlQueryParams = (options) => {
  const params = options.split('&filters');
  const paramsArr = params?.reduce((accum, curr) => {
    if (curr?.startsWith('[')) {
      const facet: string = curr.match(/\[(.*?)]/)?.[1];
      const value: string = curr.match(/=(.+)/)?.[1];

      if (facet && value) {
        return [...accum, `${facet}=${value}`];
      }
    }

    return accum;
  }, []);

  return paramsArr.join('&');
};

/**
 * [buildSectionLinkUrl]
 * Sections in the search landing feed don't come with
 * links to the relevant page where the module should link to.
 * This method takes in the searchConfig for the section and
 * builds a link based on the search config
 */

export const buildSectionLinkUrl = (
  searchConfig: ISearchConfig,
  groups: IConstructorGroup[],
  isConsumerSearchEnabled = false,
) => {
  const { collectionId = null, filterKey, filterValue, options, query, type } = searchConfig;
  const queryParams: string = getLinkUrlQueryParams(options);
  const appendQueryParams = queryParams ? `?${queryParams}` : '';

  if (type === SEARCH_CONFIG_TYPES.COLLECTION) {
    return `/collections/${collectionId}${appendQueryParams}`;
  }

  if (type === SEARCH_CONFIG_TYPES.BRAND) {
    return `/brand/${collectionId}${appendQueryParams}`;
  }

  if (filterKey?.toLowerCase() === SEARCH_CONFIG_TYPES.BRAND) {
    return `/brand/${slugify(filterValue)}${appendQueryParams}`;
  }

  if (filterKey?.toLowerCase() === SEARCH_CONFIG_TYPES.CATEGORY) {
    return `/${slugify(filterValue)}${appendQueryParams}`;
  }

  if (filterKey?.toLowerCase() === SEARCH_CONFIG_TYPES.COLOR) {
    return `/sneakers/color/${slugify(filterValue)}${appendQueryParams}`;
  }

  if (filterKey?.toLowerCase() === SEARCH_CONFIG_TYPES.DISCOUNT_TAG) {
    return filterValue === 'under_retail'
      ? `/sneakers/under-retail${appendQueryParams}`
      : `/search?discount_tag=sale${appendQueryParams}`;
  }

  if (type === SEARCH_CONFIG_TYPES.SEARCH) {
    return `/search?query=${query}${queryParams ? `&${queryParams}` : ''}`;
  }

  if (type === SEARCH_CONFIG_TYPES.BROWSE && filterKey === 'group_id') {
    if (isConsumerSearchEnabled) {
      const taxonomyPath = findTaxonomyPath(filterValue);
      if (taxonomyPath) {
        return `${taxonomyPath}${appendQueryParams}`;
      }
    } else {
      let base = `/${filterValue}`;
      let currentGroup = groups?.[0];
      if (currentGroup?.parents?.length > 1) {
        while (currentGroup?.parents) {
          base = `/${currentGroup?.parents?.[1]?.group_id}${base}`;
          currentGroup = currentGroup?.parents?.[1];
        }
      }
      return `${base}${appendQueryParams}`;
    }
  }
};
